import { type SubmitEvent, useRef, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEmailRequestSchema, type PublicRegistrationStatus } from '@lsc/shared';
import { apiRequest } from '../../api/client';
import { isApiError } from '../../api/errors';
import { publicQueryKeys } from '../api';
import { ActionError } from '../components/ActionError';
import { PageHeading } from '../components/PageHeading';
import { Turnstile, type TurnstileHandle } from '../../components/Turnstile';
import { useSecondsUntil } from '../hooks';

const EmailSchema = ChangeEmailRequestSchema.shape.email;

const dateTimeFormat = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

export interface PendingViewProps {
  registration: PublicRegistrationStatus;
  /** Polling stopped (3 min) – offer a manual refresh. */
  timedOut: boolean;
  onRefresh: () => void;
}

/** Pending page (§51): masked email, resend with cooldown (§20), email correction (§19). */
export function PendingView({ registration, timedOut, onRefresh }: PendingViewProps) {
  const queryClient = useQueryClient();
  const turnstileRef = useRef<TurnstileHandle>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileMissing, setTurnstileMissing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const onSettled = () => {
    turnstileRef.current?.reset();
  };
  const resend = useMutation({
    mutationFn: (token: string) =>
      apiRequest('resendVerification', { body: { turnstileToken: token } }),
    onSuccess: (data) => {
      queryClient.setQueryData(publicQueryKeys.registrationStatus, data);
      setNotice(`Wir haben die E-Mail erneut an ${data.registration.maskedEmail} gesendet.`);
    },
    onSettled,
  });
  const changeEmail = useMutation({
    mutationFn: (body: { email: string; turnstileToken: string }) =>
      apiRequest('changeEmail', { body }),
    onSuccess: (data) => {
      queryClient.setQueryData(publicQueryKeys.registrationStatus, data);
      setEditing(false);
      setNotice(
        `Ihre E-Mail-Adresse wurde geändert. Wir haben eine neue Nachricht an ${data.registration.maskedEmail} gesendet. Der Link aus der vorherigen E-Mail ist nicht mehr gültig.`,
      );
    },
    onSettled,
  });

  function withToken(action: (token: string) => void) {
    if (turnstileToken === null) {
      setTurnstileMissing(true);
      return;
    }
    setNotice(null);
    resend.reset();
    changeEmail.reset();
    action(turnstileToken);
  }

  function handleTurnstileToken(token: string | null) {
    setTurnstileToken(token);
    if (token !== null) setTurnstileMissing(false);
  }

  const actionsAvailable = registration.canResend || registration.canChangeEmail;
  const actionError = resend.error ?? (editing ? null : changeEmail.error);

  return (
    <Stack spacing={3}>
      <Box>
        <PageHeading>Bitte bestätigen Sie Ihre E-Mail-Adresse.</PageHeading>
        <Typography>Wir haben eine Nachricht an</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', my: 1, wordBreak: 'break-all' }}>
          {registration.maskedEmail}
        </Typography>
        <Typography>
          gesendet. Bitte klicken Sie auf den Link in der E-Mail, um Ihren Zugang zu aktivieren.
        </Typography>
        {registration.verificationExpiresAt ? (
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Der Link ist gültig bis{' '}
            {dateTimeFormat.format(new Date(registration.verificationExpiresAt))} Uhr.
          </Typography>
        ) : null}
      </Box>

      {registration.emailBounced ? (
        <Alert severity="warning">
          Die E-Mail konnte nicht zugestellt werden. Bitte prüfen Sie Ihre E-Mail-Adresse und
          korrigieren Sie sie gegebenenfalls.
        </Alert>
      ) : null}

      {notice ? (
        <Alert severity="success" role="status">
          {notice}
        </Alert>
      ) : null}

      {timedOut ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={onRefresh}>
              Status aktualisieren
            </Button>
          }
        >
          Haben Sie Ihre E-Mail-Adresse bereits bestätigt? Dann aktualisieren Sie bitte den Status.
        </Alert>
      ) : null}

      <Divider />

      <Stack spacing={2}>
        <Typography variant="h2" component="h2">
          Keine E-Mail erhalten?
        </Typography>
        <Typography color="text.secondary">
          Bitte prüfen Sie auch Ihren Spam-Ordner. Die Zustellung kann einige Minuten dauern.
        </Typography>

        {actionsAvailable ? (
          <Box>
            <Turnstile
              ref={turnstileRef}
              action="registration-pending"
              onToken={handleTurnstileToken}
            />
            {turnstileMissing ? (
              <Typography role="alert" color="error" variant="body2">
                Bitte bestätigen Sie zuerst die Sicherheitsprüfung.
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Typography color="text.secondary">
            Die maximale Anzahl an Bestätigungsmails ist erreicht. Bitte wenden Sie sich an Ihren
            Ansprechpartner, falls Sie keine E-Mail erhalten haben.
          </Typography>
        )}

        {actionError ? <ActionError error={actionError} /> : null}

        {registration.canResend ? (
          <ResendButton
            key={registration.resendAvailableAt ?? 'now'}
            availableAt={registration.resendAvailableAt}
            loading={resend.isPending}
            onClick={() => {
              withToken((token) => {
                resend.mutate(token);
              });
            }}
          />
        ) : null}

        {registration.canChangeEmail && !editing ? (
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              changeEmail.reset();
              setEditing(true);
            }}
          >
            E-Mail-Adresse korrigieren
          </Button>
        ) : null}

        {editing ? (
          <ChangeEmailForm
            loading={changeEmail.isPending}
            serverError={changeEmail.error}
            onCancel={() => {
              setEditing(false);
              changeEmail.reset();
            }}
            onSubmit={(email) => {
              withToken((token) => {
                changeEmail.mutate({ email, turnstileToken: token });
              });
            }}
          />
        ) : null}
      </Stack>
    </Stack>
  );
}

function ResendButton({
  availableAt,
  loading,
  onClick,
}: {
  availableAt: string | null;
  loading: boolean;
  onClick: () => void;
}) {
  const secondsLeft = useSecondsUntil(availableAt);
  return (
    <Box>
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={secondsLeft > 0}
        loading={loading}
        onClick={onClick}
      >
        E-Mail erneut senden
      </Button>
      {secondsLeft > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          Erneut senden möglich in <span>{formatCountdown(secondsLeft)}</span> Minuten
        </Typography>
      ) : null}
    </Box>
  );
}

function ChangeEmailForm({
  loading,
  serverError,
  onCancel,
  onSubmit,
}: {
  loading: boolean;
  serverError: unknown;
  onCancel: () => void;
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const serverFieldError = isApiError(serverError) ? serverError.fieldError('email') : undefined;
  const shownFieldError = fieldError ?? serverFieldError ?? null;

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = EmailSchema.safeParse(email);
    if (!result.success) {
      setFieldError(result.error.issues[0]?.message ?? 'Bitte prüfen Sie Ihre Eingabe.');
      document.getElementById('new-email')?.focus();
      return;
    }
    onSubmit(result.data);
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      aria-labelledby="change-email-heading"
      sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}
    >
      <Stack spacing={2}>
        <Typography id="change-email-heading" variant="h3" component="h3">
          E-Mail-Adresse korrigieren
        </Typography>
        <TextField
          id="new-email"
          label="Neue E-Mail-Adresse"
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldError(null);
          }}
          error={shownFieldError !== null}
          helperText={shownFieldError}
          autoComplete="email"
          slotProps={{
            htmlInput: { inputMode: 'email', autoCapitalize: 'none', spellCheck: false },
          }}
        />
        {serverError && serverFieldError === undefined ? <ActionError error={serverError} /> : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button type="submit" variant="contained" size="large" loading={loading}>
            Neue Adresse speichern
          </Button>
          <Button variant="text" size="large" onClick={onCancel}>
            Abbrechen
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
